import React from "react";
import { motion } from "framer-motion";
import { useLanguage } from "@/context/LanguageContext";
import { TEAM } from "@/data/team";

function TeamCard({ m, lang, idx }) {
  const role = m[`role_${lang}`] || m.role_en;
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.4, delay: Math.min(idx * 0.04, 0.35) }}
      className="group relative overflow-hidden rounded-xl border border-black/5 bg-white shadow-[0_4px_18px_rgba(0,0,0,0.04)] transition-all hover:-translate-y-1 hover:border-[#F37021]/30 hover:shadow-[0_22px_60px_rgba(0,0,0,0.08)] sm:rounded-2xl dark:border-white/10 dark:bg-[#121212] dark:hover:bg-[#1B1B1B]"
      data-testid={`team-card-${m.id}`}
    >
      <div className="relative aspect-[4/5] overflow-hidden bg-[#F2F2F2]">
        <img
          src={m.img}
          alt={m.name}
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover object-[center_18%] group-hover:scale-105 transition-transform duration-500"
        />
      </div>
      <div className="p-2 sm:p-4 md:p-5 text-center">
        <div className="font-display text-[11px] font-bold leading-tight text-[#111] sm:text-base md:text-lg dark:text-white">
          {m.name}
        </div>
        <div className="mt-0.5 sm:mt-1.5 text-[9px] sm:text-[11px] md:text-xs tracking-[0.14em] uppercase text-[#F37021] font-semibold leading-snug">
          {role}
        </div>
      </div>
    </motion.div>
  );
}

export default function TeamSection() {
  const { t, lang } = useLanguage();
  const T = t.team;

  return (
    <section
      id="about"
      data-testid="team-section"
      className="bg-white px-5 py-20 md:px-8 md:py-28 dark:bg-[#080808]"
    >
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.5 }}
          className="text-center max-w-3xl mx-auto mb-12 md:mb-16"
        >
          <h2 className="mb-5 font-display text-4xl font-bold leading-[1.08] text-[#111] sm:text-5xl md:text-6xl dark:text-white">
            {T.title}
          </h2>
          <p className="text-base leading-relaxed text-[#555] md:text-lg dark:text-[#BAC4D4]">
            {T.subtitle}
          </p>
        </motion.div>

        {/* 3 columns on every breakpoint — CEO row first, then rest.
            Forced dir="ltr" so member positions remain identical across
            EN/FR (LTR) and AR (RTL). This guarantees Mohyeldin Berjawi
            always sits on the RIGHT and Mohamad Itani on the LEFT. */}
        <div
          dir="ltr"
          className="grid grid-cols-3 gap-2.5 sm:gap-5 md:gap-6"
          data-testid="team-grid"
        >
          {TEAM.map((m, i) => (
            <TeamCard key={m.id} m={m} lang={lang} idx={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
